/**
 * Global multi-country landed-cost engine — CARICOM tariff matrix + profit brackets + FX.
 * Mirrors backend/lib/global_financial_engine.py
 */

export const BASE_CROSS_BORDER_AIR_PER_LB = 5.25;
export const PORT_CONSOLIDATION_FIXED = 12.0;
export const MIN_CARGO_INSURANCE = 3.0;
export const CARGO_INSURANCE_RATE = 0.02;
export const MIN_WEIGHT_LBS = 0.25;
export const BASELINE_CORPORATE_YIELD = 80.0;

export const DEFAULT_TARIFF = {
  country_code: 'BB',
  country_name: 'Barbados',
  category_slug: 'baby_essentials',
  import_duty_pct: 20.0,
  local_vat_gct_pct: 15.0,
  environmental_levy_pct: 1.0,
  customs_processing_flat: 15.0,
  local_currency_code: 'USD',
  currency_exchange_rate: 1.0,
};

export const DEFAULT_BRACKET = {
  base_profit_floor: 80.0,
  variable_markup_pct: 15.0,
  handling_fee_flat: 8.5,
};

export const EMBEDDED_BRACKETS = [
  { min_cost: 0, max_cost: 25, base_profit_floor: 8, variable_markup_pct: 20, handling_fee_flat: 5 },
  { min_cost: 25.01, max_cost: 100, base_profit_floor: 40, variable_markup_pct: 18, handling_fee_flat: 5 },
  { min_cost: 100.01, max_cost: 999999.99, base_profit_floor: 80, variable_markup_pct: 15, handling_fee_flat: 8.5 },
];

export const EMBEDDED_TARIFFS = {
  BB: {
    baby_essentials: { country_name: 'Barbados', import_duty_pct: 20, local_vat_gct_pct: 17.5, environmental_levy_pct: 1, customs_processing_flat: 15, local_currency_code: 'BBD', currency_exchange_rate: 2 },
    solar_power: { country_name: 'Barbados', import_duty_pct: 5, local_vat_gct_pct: 17.5, environmental_levy_pct: 1, customs_processing_flat: 25, local_currency_code: 'BBD', currency_exchange_rate: 2 },
    electronics: { country_name: 'Barbados', import_duty_pct: 40, local_vat_gct_pct: 17.5, environmental_levy_pct: 1, customs_processing_flat: 25, local_currency_code: 'BBD', currency_exchange_rate: 2 },
    apparel: { country_name: 'Barbados', import_duty_pct: 25, local_vat_gct_pct: 17.5, environmental_levy_pct: 1, customs_processing_flat: 20, local_currency_code: 'BBD', currency_exchange_rate: 2 },
  },
  JM: {
    baby_essentials: { country_name: 'Jamaica', import_duty_pct: 10, local_vat_gct_pct: 15, environmental_levy_pct: 0.5, customs_processing_flat: 2500, local_currency_code: 'JMD', currency_exchange_rate: 155.5 },
    solar_power: { country_name: 'Jamaica', import_duty_pct: 0, local_vat_gct_pct: 15, environmental_levy_pct: 0, customs_processing_flat: 3500, local_currency_code: 'JMD', currency_exchange_rate: 155.5 },
    electronics: { country_name: 'Jamaica', import_duty_pct: 20, local_vat_gct_pct: 15, environmental_levy_pct: 0.5, customs_processing_flat: 3000, local_currency_code: 'JMD', currency_exchange_rate: 155.5 },
    apparel: { country_name: 'Jamaica', import_duty_pct: 15, local_vat_gct_pct: 15, environmental_levy_pct: 0.5, customs_processing_flat: 2500, local_currency_code: 'JMD', currency_exchange_rate: 155.5 },
  },
  TT: {
    baby_essentials: { country_name: 'Trinidad & Tobago', import_duty_pct: 20, local_vat_gct_pct: 12.5, environmental_levy_pct: 1, customs_processing_flat: 100, local_currency_code: 'TTD', currency_exchange_rate: 6.75 },
    solar_power: { country_name: 'Trinidad & Tobago', import_duty_pct: 0, local_vat_gct_pct: 12.5, environmental_levy_pct: 0, customs_processing_flat: 150, local_currency_code: 'TTD', currency_exchange_rate: 6.75 },
    electronics: { country_name: 'Trinidad & Tobago', import_duty_pct: 30, local_vat_gct_pct: 12.5, environmental_levy_pct: 1, customs_processing_flat: 120, local_currency_code: 'TTD', currency_exchange_rate: 6.75 },
    apparel: { country_name: 'Trinidad & Tobago', import_duty_pct: 20, local_vat_gct_pct: 12.5, environmental_levy_pct: 1, customs_processing_flat: 100, local_currency_code: 'TTD', currency_exchange_rate: 6.75 },
  },
  LC: {
    baby_essentials: { country_name: 'St. Lucia', import_duty_pct: 15, local_vat_gct_pct: 12.5, environmental_levy_pct: 1, customs_processing_flat: 45, local_currency_code: 'XCD', currency_exchange_rate: 2.7 },
    solar_power: { country_name: 'St. Lucia', import_duty_pct: 0, local_vat_gct_pct: 12.5, environmental_levy_pct: 0, customs_processing_flat: 50, local_currency_code: 'XCD', currency_exchange_rate: 2.7 },
    electronics: { country_name: 'St. Lucia', import_duty_pct: 25, local_vat_gct_pct: 12.5, environmental_levy_pct: 1, customs_processing_flat: 50, local_currency_code: 'XCD', currency_exchange_rate: 2.7 },
    apparel: { country_name: 'St. Lucia', import_duty_pct: 15, local_vat_gct_pct: 12.5, environmental_levy_pct: 1, customs_processing_flat: 45, local_currency_code: 'XCD', currency_exchange_rate: 2.7 },
  },
  AG: {
    baby_essentials: { country_name: 'Antigua & Barbuda', import_duty_pct: 15, local_vat_gct_pct: 15, environmental_levy_pct: 1, customs_processing_flat: 40, local_currency_code: 'XCD', currency_exchange_rate: 2.7 },
    solar_power: { country_name: 'Antigua & Barbuda', import_duty_pct: 0, local_vat_gct_pct: 15, environmental_levy_pct: 0, customs_processing_flat: 45, local_currency_code: 'XCD', currency_exchange_rate: 2.7 },
    electronics: { country_name: 'Antigua & Barbuda', import_duty_pct: 25, local_vat_gct_pct: 15, environmental_levy_pct: 1, customs_processing_flat: 45, local_currency_code: 'XCD', currency_exchange_rate: 2.7 },
    apparel: { country_name: 'Antigua & Barbuda', import_duty_pct: 15, local_vat_gct_pct: 15, environmental_levy_pct: 1, customs_processing_flat: 40, local_currency_code: 'XCD', currency_exchange_rate: 2.7 },
  },
};

/** @deprecated Never use as fallback */
export const LEGACY_FALLBACK_WHOLESALE = 35;
export const LEGACY_FALLBACK_PRICE = 42;

function round2(n) {
  return Math.round(Number(n) * 100) / 100;
}

export function normalizeCategorySlug(raw) {
  if (!raw) return 'baby_essentials';
  const slug = String(raw).toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
  return slug || 'baby_essentials';
}

export function mapToGlobalCategorySlug(raw) {
  const slug = normalizeCategorySlug(raw);
  const legacyMap = {
    general_merchandise: 'baby_essentials',
    health_beauty: 'baby_essentials',
    consumer_electronics: 'electronics',
    luxury_jewelry: 'apparel',
    apparel_textiles: 'apparel',
  };
  const allCategories = new Set(['baby_essentials', 'solar_power', 'electronics', 'apparel']);
  if (allCategories.has(slug)) return slug;
  return legacyMap[slug] || 'baby_essentials';
}

export function inferCategorySlug(title = '', category = '') {
  if (category) return mapToGlobalCategorySlug(category);
  const text = String(title).toLowerCase();
  if (/solar|panel|inverter|photovoltaic/.test(text)) return 'solar_power';
  if (/phone|laptop|electronic|tablet|camera|tv|console|headphone/.test(text)) return 'electronics';
  if (/shirt|dress|shoe|apparel|textile|clothing|fashion|jewelry|watch/.test(text)) return 'apparel';
  if (/baby|shea|lotion|diaper|infant|beauty|cosmetic|skincare/.test(text)) return 'baby_essentials';
  return 'baby_essentials';
}

export function resolveGlobalTariff(destinationCountry = 'BB', categorySlug = 'baby_essentials', tariffOverride) {
  if (tariffOverride) return { ...DEFAULT_TARIFF, ...tariffOverride };
  const country = String(destinationCountry || 'BB').toUpperCase();
  const category = mapToGlobalCategorySlug(categorySlug);
  const countryMap = EMBEDDED_TARIFFS[country] || EMBEDDED_TARIFFS.BB;
  const base = countryMap[category] || countryMap.baby_essentials || DEFAULT_TARIFF;
  return { country_code: country, country_name: base.country_name, category_slug: category, ...base };
}

export function resolveProfitBracket(itemCost, bracketOverride) {
  if (bracketOverride) return { ...DEFAULT_BRACKET, ...bracketOverride };
  const cost = Number(itemCost) || 0;
  for (const bracket of EMBEDDED_BRACKETS) {
    if (cost >= bracket.min_cost && cost <= bracket.max_cost) return { ...bracket };
  }
  return { ...DEFAULT_BRACKET };
}

export function calculateGlobalLandedCost(input) {
  const cost = Math.max(Number(input.rawMarketplacePrice ?? input.itemCost) || 0, 0.01);
  const weight = Math.max(Number(input.weightLbs) || 0, MIN_WEIGHT_LBS);
  const tariff = resolveGlobalTariff(
    input.destinationCountry || 'BB',
    input.categorySlug || 'baby_essentials',
    input.tariff,
  );
  const bracket = resolveProfitBracket(cost, input.bracket);

  const insurance = Math.max(MIN_CARGO_INSURANCE, cost * CARGO_INSURANCE_RATE);
  const freight = PORT_CONSOLIDATION_FIXED + weight * BASE_CROSS_BORDER_AIR_PER_LB;
  const cif = cost + insurance + freight;

  const duty = cif * (Number(tariff.import_duty_pct) / 100);
  const envLevy = cif * (Number(tariff.environmental_levy_pct) / 100);
  const fx = Math.max(Number(tariff.currency_exchange_rate), 0.0001);
  const localProcessingUsd = Number(tariff.customs_processing_flat) / fx;

  const vatBase = cif + duty + envLevy;
  const vat = vatBase * (Number(tariff.local_vat_gct_pct) / 100);
  const borderFees = duty + envLevy + vat + localProcessingUsd;

  const profit =
    Number(bracket.base_profit_floor) +
    cost * (Number(bracket.variable_markup_pct) / 100) +
    Number(bracket.handling_fee_flat);
  const finalUsd = cif + borderFees + profit;

  const local = {
    itemCost: round2(cost * fx),
    logisticsAndFreight: round2(freight * fx),
    customsAndDuties: round2(borderFees * fx),
    grandTotalLanded: round2(finalUsd * fx),
  };

  return {
    baseCurrency: 'USD',
    localCurrency: tariff.local_currency_code,
    exchangeRateUsed: round2(fx),
    destinationCountry: tariff.country_code,
    countryName: tariff.country_name,
    categorySlug: tariff.category_slug,
    priceUSD: round2(finalUsd),
    netProfitUSD: round2(profit),
    computedCIFUSD: round2(cif),
    portFeesUSD: round2(borderFees),
    rawMarketplaceCostUSD: round2(cost),
    weightLbs: round2(weight),
    paymentPayloadLocalCurrency: local,
    pricing_engine: 'global_failsafe',
  };
}

export function globalToConciergeBreakdown(globalResult) {
  const cost = Number(globalResult.rawMarketplaceCostUSD);
  const cif = Number(globalResult.computedCIFUSD);
  const insurance = Math.max(MIN_CARGO_INSURANCE, cost * CARGO_INSURANCE_RATE);
  const freight = Math.max(cif - cost - insurance, 0);
  return {
    original_cost: cost,
    margin_amount: Number(globalResult.netProfitUSD),
    customs_amount: Number(globalResult.portFeesUSD),
    freight_amount: round2(freight + insurance),
    calculated_landed_price: Number(globalResult.priceUSD),
    weight_lbs: Number(globalResult.weightLbs || MIN_WEIGHT_LBS),
    pricing_engine: 'global_failsafe',
    guaranteed_net_profit: Number(globalResult.netProfitUSD),
    computed_cif: cif,
    port_fees_and_duties: Number(globalResult.portFeesUSD),
    global_pricing: globalResult,
  };
}

export function calculateConciergeLandedPrice(
  extractedCostPrice,
  estimatedWeightLbs,
  destinationCountry = 'BB',
  categorySlug = 'baby_essentials',
  title = '',
) {
  const category = mapToGlobalCategorySlug(categorySlug || inferCategorySlug(title));
  const globalResult = calculateGlobalLandedCost({
    rawMarketplacePrice: extractedCostPrice,
    weightLbs: estimatedWeightLbs,
    destinationCountry,
    categorySlug: category,
  });
  return globalToConciergeBreakdown(globalResult);
}

export function buildGlobalCheckoutSummary(source) {
  const global = source?.global_pricing || source?.globalPricing;
  if (global?.priceUSD != null) {
    return {
      priceUSD: global.priceUSD,
      netProfitUSD: global.netProfitUSD,
      localCurrency: global.localCurrency,
      exchangeRateUsed: global.exchangeRateUsed,
      localTotal: global.paymentPayloadLocalCurrency?.grandTotalLanded,
      localPayload: global.paymentPayloadLocalCurrency,
      countryName: global.countryName,
      destinationCountry: global.destinationCountry,
      rawMarketplaceCost: global.rawMarketplaceCostUSD,
      portFeesAndDuties: global.portFeesUSD,
      computedCIF: global.computedCIFUSD,
      customerFinalLandedPrice: global.priceUSD,
    };
  }
  const cost = Number(source?.source_price || source?.original_cost || 0);
  if (!(cost > 0)) return null;
  const computed = calculateGlobalLandedCost({
    rawMarketplacePrice: cost,
    weightLbs: source?.estimated_weight_lbs || source?.weight_lbs || 0.5,
    destinationCountry: source?.destination || source?.destination_country || 'BB',
    categorySlug: inferCategorySlug(source?.title || source?.name || '', source?.category_slug),
  });
  return buildGlobalCheckoutSummary({ global_pricing: computed });
}
